import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import FormDrawer from "components/__new/common/form-drawer";
import AccountInfo from "components/__new/payments/AccountInfo";
import { ReactComponent as IconBank } from "assets/images/icons/project-icons/bank.svg";
import { ReactComponent as IconCard } from "assets/images/icons/project-icons/card-3.svg";
import { ReactComponent as IconChevronLeft } from "assets/images/icons/project-icons/chevron-left.svg";
import { fundBusinessAccount } from "store/entities/wallet/action";
import * as notificationAction from "store/entities/notification/action";
import useLoginInfo from "hooks/useLoginInfo";
import Maybe from "../common/Maybe";
import { ButtonLoader, Input } from "../common/form";
import ContentLoading from "../common/ContentLoading";
import ReactTooltip from 'react-tooltip';
import copy from "copy-to-clipboard";
import { toast } from "react-toastify";
import { ReactComponent as IconCopy } from "assets/images/icons/project-icons/copy.svg";
import { ReactComponent as IconInfo } from "assets/images/icons/icon-info-new.svg";


const FundAccount = (props) => {
    const dispatch = useDispatch();
    const { userInfo } = useLoginInfo();
    const { fundBusinessAccountLoading, fundBusinessAccount: fundBusinessAccountData } = useSelector((state) => state?.entities?.wallet);
    const {isOpen, setIsOpen, dashboardLoading, dashboard} = props;
    const [isOpenAmount, setIsOpenAmount] = useState(false);
    const formDefault = {
        amount: "",
    }
    const [form, setForm] = useState(formDefault)

    const handleFundBusinessAccount = () => {
        setIsOpen(false);
        setIsOpenAmount(true);
    }
    
    const copyText = (textToCopy) => {
        let content = dashboard?.data?.walletDetails?.oldAccountNumber ?? "-";
        if (textToCopy){
            content = textToCopy;
        }
        copy(content);
        toast.success("Copied: " + content);
    }

    const submitForm = (e) => {
        // Send walletId and amount
        e.preventDefault();
        const payload = {
            walletId: String(dashboard?.data?.walletDetails?.walletId),
            amount: Number(form?.amount?.split(",").join(""))
        }

        dispatch(fundBusinessAccount(payload))
        dispatch(notificationAction.getAllNotifications())
    }

    // Redirect the business once payment link is available
    useEffect(() => {
        if (fundBusinessAccountData?.data?.paymentLinkUrl) {
            window.location.href = fundBusinessAccountData?.data?.paymentLinkUrl;
        }
    }, [fundBusinessAccountData]);

    return (
        <>
            <Maybe condition={isOpenAmount === true}>
                <FormDrawer display="center" size="lg" isOpen={isOpenAmount} setIsOpen={setIsOpenAmount}>
                    <div className="p-6">
                        <FormDrawer.Title
                            title="Enter Amount"
                            showCloseButton={true}
                            close={setIsOpenAmount}
                        />

                        <hr className="w-full h-px mt-2 bg-[#E2E4E8] border-none" />
                        <form onSubmit={submitForm} className="mt-8">
                            <Input
                                type="text"
                                label="Amount (Naira)"
                                placeholder="0.00"
                                inputClass={"pl-4"}
                                value={form?.amount}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    if (value === "0") return;
                                    const cleanedValue = value.replace(/[^\d.]/g, '');
                                    const [integerPart, decimalPart] = cleanedValue.split('.');
                                    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                    let formattedValue = formattedInteger;
                                    if (decimalPart !== undefined) {
                                        formattedValue += '.' + decimalPart.slice(0, 2);
                                    }
                                    setForm({
                                        ...form, 
                                        amount: formattedValue,
                                    })
                                }}
                            />
                            <div className="mt-10 flex justify-end space-x-4">
                                <button type="button" onClick={() => setIsOpenAmount(false)} className="btn btn-lg btn-transparent-primary">
                                    Cancel
                                </button>
                                <ButtonLoader type="submit" loading={fundBusinessAccountLoading} className="btn btn-lg btn-ep-primary">
                                    Continue
                                </ButtonLoader>
                            </div>
                        </form>
                    </div>
                </FormDrawer>
            </Maybe>

            {/* Business Account Details */}
            <FormDrawer display="center" size={`${dashboard?.data?.walletDetails?.oldBankName ? "4xl" : "lg"}`} isOpen={isOpen} setIsOpen={setIsOpen}>
                <div className="p-6">
                    <FormDrawer.Title
                        title="Fund Account"
                        description="Add money to your business account"
                        showCloseButton={true}
                        close={setIsOpen}
                    />
                    
                    <hr className="w-full h-px mt-2 bg-[#E2E4E8] border-none" />
                    
                    <div className="p-4 mt-4 bg-ep-primary-light-100 rounded-lg">
                        <div className="flex items-center">
                            <div className="w-12 h-12 mr-4 flex flex-shrink-0 border border-[#D1D5DB] rounded-full">
                                <IconBank className="m-auto" />
                            </div>
                            <div className="flex-grow">
                                <div className="font-medium">
                                    Bank Transfer
                                </div>
                                <div className="text-[#27272E] text-sm">
                                    Transfer to the bank account below
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className={`mt-6 flex flex-col flex-1 rounded-xl border p-3 bg-[#FFFFFF] space-y-4`}>
                                <AccountInfo
                                    loading={dashboardLoading}
                                    label="Account Number"
                                    data={dashboard?.data?.walletDetails?.accountNumber ?? "-"}
                                    showCopyIcon={true}
                                />
                                <AccountInfo
                                    loading={dashboardLoading}
                                    label="Account Name"
                                    data={dashboard?.data?.walletDetails?.accountName ?? "-"}
                                />
                                <AccountInfo
                                    loading={dashboardLoading}
                                    label="Bank Name"
                                    data={<span className="capitalize">{dashboard?.data?.walletDetails?.bankName ?? "-"}</span>}
                                    textToCopy={dashboard?.data?.walletDetails?.bankName ?? ""}
                                />
                            </div>

                            {
                                dashboard?.data?.walletDetails?.oldBankName && 
                                <div className="mt-6 flex flex-col flex-1 rounded-xl border p-3 space-y-4 bg-[#FFFFFF]">
                                    <div className="">
                                        <div className="text-[#717171] flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <p>Account Number</p>
                                                {dashboard?.data?.walletDetails?.oldBankName && <p className="text-[#B42318] bg-[#FEF3F2] rounded-2xl py-0.5 px-2.5">Old Account</p>}
                                            </div>
                                            <Maybe condition={true}>
                                            <button type="button" className="w-5 h-5" data-tip data-for="old-account"><IconInfo /></button>
                                                <ReactTooltip id="old-account" place="top" effect="solid" className="custom-tooltip" offset={{ top: 10, left: 10 }}>
                                                    <p className="text-[14px]">
                                                        We have switched our accounts provider to Stanbic IBTC. Please <br /> 
                                                        note that Providus accounts will no longer be available from 1st <br />
                                                        September 2024. <br />
                                                        Kindly update all your financial workflows accordingly.
                                                    </p>
                                                </ReactTooltip>
                                            </Maybe>
                                        </div>
                                        <div className={`mt-0.5 font-medium flex items-center ${"Account Number" ? "text-2xl" : ""}`}>
                                            <ContentLoading
                                                loading={dashboardLoading}
                                                data={dashboard?.data?.walletDetails?.oldAccountNumber}
                                            />
                                            <Maybe condition={dashboardLoading === false && dashboard?.data?.walletDetails?.oldAccountNumber}>
                                                <Maybe condition={true}>
                                                    <IconCopy onClick={() => copyText(dashboard?.data?.walletDetails?.oldAccountNumber)} className="text-ep-primary-600 ml-3 cursor-pointer w-6 h-6" />
                                                </Maybe>
                                            </Maybe>
                                        </div>
                                    </div>
                                    <AccountInfo
                                    loading={dashboardLoading}
                                    label="Account Name"
                                    data={dashboard?.data?.walletDetails?.oldAccountName ?? "-"}
                                    />
                                    <AccountInfo
                                        loading={dashboardLoading}
                                        label="Bank Name"
                                        data={<span className="capitalize">{dashboard?.data?.walletDetails?.oldBankName ?? "-"}</span>}
                                        textToCopy={dashboard?.data?.walletDetails?.oldBankName ?? ""}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                    
                    <div onClick={handleFundBusinessAccount} className="p-4 mt-4 flex items-center bg-ep-primary-light-100 rounded-lg cursor-pointer">
                        <div className="w-12 h-12 mr-4 flex flex-shrink-0 border border-[#D1D5DB] rounded-full">
                            <IconCard className="m-auto" />
                        </div>
                        <div className="flex-grow">
                            <div className="font-medium">
                                Credit or Debit Card
                            </div>
                            <div className="text-[#27272E] text-sm">
                                Add money using a debit or credit card
                            </div>
                        </div>
                        <div className="w-6 h-full flex-shrink-0 flex justify-end items-center">
                            <IconChevronLeft className="svg-fill-black" />
                        </div>
                    </div>
                </div>
            </FormDrawer>
        </>
    )
}


export default FundAccount;